/**
 * Adapts a SchemaDraft into a FormSchemaVersionDetailData-shaped object that
 * DynamicFormRenderer can consume. The conversion is lossless w.r.t. rendering
 * AND propagates UI ids so the editor's selection wrapper can dispatch
 * mutations against `useSchemaDraft` without a parallel ID lookup.
 *
 * Two extras are stamped onto each field/section beyond what the renderer
 * needs: `__uiId` and (on fields) `__sectionUiId`. The renderer ignores
 * unknown properties, so layering these on is safe.
 *
 * Empty keys (newly added fields with no label) get a synthetic `__ui_<id>`
 * fallback so React's keyed-iteration works and `errors[key]` lookups don't
 * collide. This synthetic key never reaches the wire — the editor renders
 * a draft, and `draftToSchemaCommand` (in `schema-draft.ts`) is what builds
 * the actual save payload.
 */

import type {
  FormFieldData,
  FormSchemaVersionDetailData,
  FormSectionData,
} from '@/services/api-types';
import type { FieldDraft, SchemaDraft, SectionDraft } from '../builder/schema-draft';

export interface EditorFieldData extends FormFieldData {
  __uiId: string;
  __sectionUiId: string;
}

export interface EditorSectionData extends FormSectionData {
  __uiId: string;
}

export interface EditorSchemaVersion extends FormSchemaVersionDetailData {
  sections: EditorSectionData[];
}

function fieldDraftToData(field: FieldDraft, sectionUiId: string): EditorFieldData {
  const out: EditorFieldData = {
    key: field.key || `__ui_${field.uiId}`,
    label: field.label,
    type: field.type,
    __uiId: field.uiId,
    __sectionUiId: sectionUiId,
  };
  if (field.required) out.required = true;
  if (field.searchable) out.searchable = true;
  if (field.summary) out.summary = true;
  if (field.showIf) out.showIf = field.showIf;
  if (field.options && field.options.length > 0) out.options = field.options.map((o) => ({ ...o }));
  if (field.columns && field.columns.length > 0) {
    // Columns nest under the parent table and get edited via the inspector drawer,
    // not directly on the canvas. They still need a synthetic key fallback so the
    // table renderer can iterate them.
    out.columns = field.columns.map((c) => fieldDraftToData(c, sectionUiId)) as FormFieldData[];
  }
  if (field.formula) out.formula = field.formula;
  if (field.subheading !== undefined) out.subheading = field.subheading;
  if (field.inlineWithPrevious) out.inlineWithPrevious = true;
  if (field.predefinedRows && field.predefinedRows.length > 0) {
    out.predefinedRows = field.predefinedRows.map((r) => ({ ...r }));
  }
  if (field.wide) out.wide = true;
  if (field.format) out.format = field.format;
  if (field.layout === 'horizontal') out.layout = 'horizontal';
  return out;
}

function sectionDraftToData(s: SectionDraft): EditorSectionData {
  const out: EditorSectionData = {
    id: s.id || `__ui_section_${s.uiId}`,
    title: s.title,
    fields: s.fields.map((f) => fieldDraftToData(f, s.uiId)),
    __uiId: s.uiId,
  };
  if (s.sidebar) out.sidebar = true;
  return out;
}

export function draftToEditorSchema(draft: SchemaDraft): EditorSchemaVersion {
  return {
    id: 0,
    versionNumber: 1,
    dateCreated: new Date().toISOString(),
    createdById: 0,
    sections: draft.sections.map(sectionDraftToData),
  };
}

/** Type guard — every field passed through `draftToEditorSchema` has a __uiId. */
export function isEditorField(f: FormFieldData): f is EditorFieldData {
  return typeof (f as EditorFieldData).__uiId === 'string';
}

export function isEditorSection(s: FormSectionData): s is EditorSectionData {
  return typeof (s as EditorSectionData).__uiId === 'string';
}
